import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createOrUpdateStore, fetchMyStore } from '../store/storeSlice';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import {
  Palette, Layout, Shield, Check, Sparkles, Settings as SettingsIcon,
  Type, Square, Clock, ChevronRight, RotateCcw, Loader2, Star,
  Shirt, UtensilsCrossed, ShoppingCart, Calendar, Download,
  Eye, Lock, Edit2,
} from 'lucide-react';
import './SiteCustomization.css';

/* ─── Theme data (free) ──────────────────────────────────────── */
const THEMES = [
  { id: 'MODERN',  name: 'Modern',  description: 'Clean & minimalist design with a bold blue palette',  primaryColor: '#2563eb', isNew: false },
  { id: 'ELEGANT', name: 'Elegant', description: 'Sophisticated purple theme for premium brands',         primaryColor: '#7c3aed', isNew: true  },
  { id: 'MINIMAL', name: 'Minimal', description: 'Ultra-clean green accent for modern shops',             primaryColor: '#059669', isNew: false },
  { id: 'CLASSIC', name: 'Classic', description: 'Traditional red theme with timeless appeal',            primaryColor: '#dc2626', isNew: true  },
];

const LAYOUT_TEMPLATES = [
  {
    id: 'fashion',
    name: 'Fashion',
    description: 'Large hero images, lookbook-style grid, minimal text.',
    color: '#7c3aed',
    Icon: Shirt,
    mockup: (c) => (
      <div className="stcz-mock">
        <div className="stcz-mock-hero" style={{ background: `linear-gradient(135deg, ${c}18, ${c}30)` }}>
          <span className="stcz-mock-stripe" style={{ background: c }} />
        </div>
        <div className="stcz-mock-grid stcz-mock-grid-2x2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ background: `${c}10`, border: `1px solid ${c}15`, borderRadius: 3 }} />
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'restaurant',
    name: 'Restaurant / Food',
    description: 'Menu-style layout, food photography focus, category tabs.',
    color: '#ea580c',
    Icon: UtensilsCrossed,
    mockup: (c) => (
      <div className="stcz-mock">
        <div className="stcz-mock-tabs">
          {[0, 1, 2].map(i => (
            <span key={i} style={{ flex: i === 0 ? 1.5 : 1, background: i === 0 ? c : `${c}25` }} />
          ))}
        </div>
        <div className="stcz-mock-rows">
          {[1, 2, 3].map(i => (
            <div key={i} className="stcz-mock-row">
              <span className="stcz-mock-row-img" style={{ background: `${c}12` }} />
              <span className="stcz-mock-row-lines">
                <span style={{ background: '#e5e7eb', width: '70%' }} />
                <span style={{ background: '#f3f4f6', width: '40%' }} />
              </span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'grocery',
    name: 'Grocery / Supermarket',
    description: 'Department navigation, deal banners, quick-add buttons.',
    color: '#16a34a',
    Icon: ShoppingCart,
    mockup: (c) => (
      <div className="stcz-mock">
        <div className="stcz-mock-banner" style={{ background: `${c}15` }}>
          <span style={{ background: c, opacity: 0.4 }} />
        </div>
        <div className="stcz-mock-grid stcz-mock-grid-3x2">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="stcz-mock-cell" style={{ background: `${c}08`, borderColor: `${c}12` }}>
              <span style={{ background: `${c}30` }} />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'services',
    name: 'Services / Booking',
    description: 'Service cards, booking calendar integration, team showcase.',
    color: '#0284c7',
    Icon: Calendar,
    mockup: (c) => (
      <div className="stcz-mock">
        <div className="stcz-mock-svc">
          {[1, 2, 3].map(i => (
            <span key={i} style={{ background: `${c}10`, borderColor: `${c}15` }}>
              <span className="stcz-mock-svc-dot" style={{ background: `${c}20` }} />
              <span className="stcz-mock-svc-line" style={{ background: `${c}20` }} />
            </span>
          ))}
        </div>
        <div className="stcz-mock-cal" style={{ background: `${c}06`, borderColor: `${c}10` }}>
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} style={{ background: i === 5 ? `${c}25` : 'transparent' }} />
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'digital',
    name: 'Digital Products',
    description: 'Download-focused, feature highlights, pricing tables.',
    color: '#9333ea',
    Icon: Download,
    mockup: (c) => (
      <div className="stcz-mock">
        <div className="stcz-mock-hero" style={{ background: `linear-gradient(135deg, ${c}12, ${c}25)`, padding: 6 }}>
          <span className="stcz-mock-stripe" style={{ background: c, opacity: 0.3, width: '80%' }} />
          <span className="stcz-mock-stripe stcz-mock-stripe-thin" style={{ background: c, opacity: 0.2, width: '50%' }} />
        </div>
        <div className="stcz-mock-tier">
          {[1, 2, 3].map(i => (
            <span key={i} style={{ background: `${c}06`, borderColor: `${c}10` }}>
              <span style={{ background: `${c}25` }} />
            </span>
          ))}
        </div>
      </div>
    ),
  },
];

const INDUSTRIES = [
  { value: 'ALL',         label: 'All industries' },
  { value: 'GENERAL',     label: 'General' },
  { value: 'FASHION',     label: 'Fashion & clothing' },
  { value: 'KIDS',        label: 'Kids & baby' },
  { value: 'AUTOS',       label: 'Auto & parts' },
  { value: 'REAL_ESTATE', label: 'Real estate' },
  { value: 'FMCG',        label: 'FMCG & groceries' },
  { value: 'TECH',        label: 'Tech & gadgets' },
  { value: 'RESTAURANT',  label: 'Restaurant & food' },
  { value: 'LUXURY',      label: 'Luxury & jewelry' },
  { value: 'HANDMADE',    label: 'Handmade & craft' },
  { value: 'SERVICES',    label: 'Services & portfolio' },
];

const FONT_OPTIONS = [
  { value: '', label: 'Theme default' },
  { value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', label: 'System (Sans-serif)' },
  { value: 'Inter, -apple-system, sans-serif',                                  label: 'Inter (Modern)' },
  { value: 'Georgia, "Times New Roman", serif',                                 label: 'Georgia (Serif)' },
  { value: '"Playfair Display", Georgia, serif',                                label: 'Playfair Display (Elegant)' },
  { value: '"Roboto", sans-serif',                                              label: 'Roboto (Clean)' },
  { value: '"Poppins", sans-serif',                                             label: 'Poppins (Friendly)' },
  { value: '"Montserrat", sans-serif',                                          label: 'Montserrat (Bold)' },
  { value: '"Open Sans", sans-serif',                                           label: 'Open Sans (Readable)' },
];

const MAIN_TABS = [
  { id: 'colors',  label: 'Free themes',    icon: Palette },
  { id: 'premium', label: 'Premium themes', icon: Star },
  { id: 'layouts', label: 'Layouts',        icon: Layout },
];

const PREMIUM_SUBTABS = [
  { id: 'all',       label: 'All' },
  { id: 'free',      label: 'Free' },
  { id: 'premium',   label: 'Premium' },
  { id: 'purchased', label: 'My themes' },
];


const formatPrice = (price) => {
  if (price === 0 || price === null) return 'Free';
  // backend prices are kobo
  return `₦${(price / 100).toLocaleString()}`;
};

/* ─── Free-theme preview card ────────────────────────────────── */
function BrowserMockup({ theme, isActive, isSelected, onSelect, onCustomize }) {
  return (
    <article
      className={`stcz-theme${isSelected ? ' is-selected' : ''}${isActive ? ' is-active' : ''}`}
      onClick={onSelect}
    >
      {theme.isNew && (
        <span className="stcz-theme-tag stcz-theme-tag-new">
          <Sparkles size={10} /> NEW
        </span>
      )}
      {isActive && (
        <span className="stcz-theme-tag stcz-theme-tag-active">
          <Check size={10} strokeWidth={3} /> Active
        </span>
      )}

      <span
        className={`stcz-theme-check${isSelected ? ' is-checked' : ''}`}
        aria-hidden="true"
      >
        {isSelected && <Check size={12} strokeWidth={3} />}
      </span>

      <div className="stcz-theme-frame">
        <div className={`stcz-theme-chrome${isActive ? ' is-active' : ''}`}>
          <span /><span /><span />
          <span className="stcz-theme-chrome-bar" />
        </div>
        <div className="stcz-theme-preview">
          <div
            className="stcz-theme-preview-hero"
            style={{ background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryColor}cc 100%)` }}
          >
            <span style={{ background: 'rgba(255,255,255,.8)' }} />
            <span style={{ background: 'rgba(255,255,255,.4)' }} />
          </div>
          <div className="stcz-theme-preview-grid">
            {[1, 2, 3].map(i => (
              <div key={i} style={{ borderColor: `${theme.primaryColor}15` }} />
            ))}
          </div>
        </div>
      </div>

      <div className="stcz-theme-info">
        <div className="stcz-theme-info-head">
          <span
            className="stcz-theme-swatch"
            style={{ background: theme.primaryColor, boxShadow: `0 0 0 1px ${theme.primaryColor}40` }}
          />
          <strong className="stcz-theme-name">{theme.name}</strong>
        </div>
        <p className="stcz-theme-desc">{theme.description}</p>
        <button
          type="button"
          className="stcz-btn stcz-btn-secondary stcz-btn-full"
          onClick={(e) => { e.stopPropagation(); onCustomize(); }}
        >
          <SettingsIcon size={13} /> Customize
        </button>
      </div>
    </article>
  );
}

/* ─── Layout template card (Coming Soon) ─────────────────────── */
function LayoutTemplateCard({ template }) {
  const Icon = template.Icon;
  return (
    <article className="stcz-layout">
      <span className="stcz-layout-tag">
        <Clock size={10} /> Coming soon
      </span>
      <div
        className="stcz-layout-preview"
        style={{ background: `${template.color}06`, borderColor: `${template.color}12` }}
      >
        <div className="stcz-layout-frame">
          <div className="stcz-layout-chrome">
            <span /><span /><span />
          </div>
          <div className="stcz-layout-body">{template.mockup(template.color)}</div>
        </div>
      </div>
      <div className="stcz-layout-info">
        <span
          className="stcz-layout-icon"
          style={{ background: `${template.color}10`, color: template.color }}
        >
          <Icon size={16} />
        </span>
        <div className="stcz-layout-text">
          <strong>{template.name}</strong>
          <em>{template.description}</em>
        </div>
      </div>
    </article>
  );
}

/* ─── Premium theme card (marketplace inside this page) ─────── */
function PremiumThemeCard({
  theme, isOwned, isActive, onActivate, onPurchase, onPreview, onCustomize,
  activating, purchasing,
}) {
  const previewImage = theme.previewImages?.[0] || null;

  return (
    <article className={`stcz-premium${isActive ? ' is-active' : ''}`}>
      <div className="stcz-premium-preview">
        {previewImage ? (
          <img src={previewImage} alt={theme.name} loading="lazy" />
        ) : (
          <div className="stcz-premium-placeholder">
            <Palette size={32} />
          </div>
        )}

        <div className="stcz-premium-badges">
          {theme.globalSettings?.marketplace?.isNew && (
            <span className="stcz-premium-badge stcz-premium-badge-new">
              <Sparkles size={10} /> New
            </span>
          )}
          {isActive && (
            <span className="stcz-premium-badge stcz-premium-badge-active">
              <Check size={10} strokeWidth={3} /> Active
            </span>
          )}
          {theme.isFeatured && (
            <span className="stcz-premium-badge stcz-premium-badge-featured">
              <Star size={10} fill="currentColor" /> Featured
            </span>
          )}
          {theme.isPremium && !isActive && (
            <span className="stcz-premium-badge stcz-premium-badge-premium">
              <Lock size={10} /> Premium
            </span>
          )}
        </div>

        <button
          type="button"
          className="stcz-premium-preview-btn"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
        >
          <Eye size={14} /> Preview
        </button>
      </div>

      <div className="stcz-premium-info">
        <div className="stcz-premium-info-head">
          <strong className="stcz-premium-name">{theme.name}</strong>
          {theme.industry && (
            <span className="stcz-premium-industry">
              {theme.industry.replace('_', ' ')}
            </span>
          )}
        </div>
        {theme.description && (
          <p className="stcz-premium-desc">{theme.description}</p>
        )}
        <div className="stcz-premium-foot">
          <span className={`stcz-premium-price${theme.price === 0 ? ' is-free' : ''}`}>
            {formatPrice(theme.price)}
          </span>
          {isActive ? (
            <button
              type="button"
              className="stcz-btn stcz-btn-secondary"
              onClick={onCustomize}
            >
              <Edit2 size={13} /> Customize
            </button>
          ) : isOwned ? (
            <button
              type="button"
              className="stcz-btn stcz-btn-primary"
              onClick={onActivate}
              disabled={activating}
            >
              {activating ? <Loader2 size={13} className="stcz-spin" /> : <Check size={13} />}
              {activating ? 'Activating…' : 'Activate'}
            </button>
          ) : (
            <button
              type="button"
              className="stcz-btn stcz-btn-primary"
              onClick={onPurchase}
              disabled={purchasing}
            >
              {purchasing ? <Loader2 size={13} className="stcz-spin" /> : null}
              {purchasing ? 'Processing…' : theme.price === 0 ? 'Use theme' : 'Purchase'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

/* ─── Theme customization modal ──────────────────────────────── */
function ThemeCustomizationModal({ theme, store, isOpen, onClose, onSave }) {
  const [customConfig, setCustomConfig] = useState({
    primaryColor:        store?.themeConfig?.primaryColor   || theme?.primaryColor || '#0d7463',
    secondaryColor:      store?.themeConfig?.secondaryColor || '#10b981',
    accentColor:         store?.themeConfig?.accentColor    || '#f59e0b',
    headingFont:         store?.themeConfig?.headingFont    || '',
    bodyFont:            store?.themeConfig?.bodyFont       || '',
    buttonBorderRadius:  store?.themeConfig?.buttonBorderRadius || '0.5rem',
    buttonPadding:       store?.themeConfig?.buttonPadding      || '0.625rem 1.25rem',
  });
  const [activeTab, setActiveTab] = useState('colors');

  useEffect(() => {
    if (!isOpen) return;
    setCustomConfig({
      primaryColor:        store?.themeConfig?.primaryColor   || theme?.primaryColor || '#0d7463',
      secondaryColor:      store?.themeConfig?.secondaryColor || '#10b981',
      accentColor:         store?.themeConfig?.accentColor    || '#f59e0b',
      headingFont:         store?.themeConfig?.headingFont    || '',
      bodyFont:            store?.themeConfig?.bodyFont       || '',
      buttonBorderRadius:  store?.themeConfig?.buttonBorderRadius || '0.5rem',
      buttonPadding:       store?.themeConfig?.buttonPadding      || '0.625rem 1.25rem',
    });
    setActiveTab('colors');
  }, [isOpen, theme, store]);

  const TABS = [
    { id: 'colors',     label: 'Colors',     icon: Palette },
    { id: 'typography', label: 'Typography', icon: Type },
    { id: 'buttons',    label: 'Buttons',    icon: Square },
  ];

  const update = (key, value) => setCustomConfig((prev) => ({ ...prev, [key]: value }));

  const ColorField = ({ label, configKey, help }) => (
    <div className="stcz-field">
      <label className="stcz-field-label">{label}</label>
      <div className="stcz-color-row">
        <input
          type="color"
          className="stcz-color-swatch"
          value={customConfig[configKey]}
          onChange={(e) => update(configKey, e.target.value)}
        />
        <input
          type="text"
          className="stcz-input stcz-color-text"
          value={customConfig[configKey]}
          onChange={(e) => update(configKey, e.target.value)}
        />
      </div>
      {help && <span className="stcz-field-hint">{help}</span>}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={theme ? `Customize ${theme.name}` : 'Customize'}
      size="lg"
      padded={false}
      footer={
        <>
          <button type="button" className="stcz-btn stcz-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="stcz-btn stcz-btn-primary"
            onClick={() => onSave(customConfig)}
          >
            <Check size={14} /> Save customisation
          </button>
        </>
      }
    >
      <div className="stcz-modal">
        <div className="stcz-modal-tabs-wrap">
          <div className="stcz-modal-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`stcz-modal-tab${activeTab === t.id ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <Icon size={13} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="stcz-modal-body">
          {activeTab === 'colors' && (
            <>
              <ColorField label="Primary color"   configKey="primaryColor"   help="Used for primary buttons, links, and accents." />
              <ColorField label="Secondary color" configKey="secondaryColor" help="Used for secondary buttons and highlights." />
              <ColorField label="Accent color"    configKey="accentColor"    help="Used for badges, callouts, and decorative elements." />
            </>
          )}

          {activeTab === 'typography' && (
            <>
              <div className="stcz-field">
                <label className="stcz-field-label">Heading font</label>
                <select
                  className="stcz-input stcz-select"
                  value={customConfig.headingFont}
                  onChange={(e) => update('headingFont', e.target.value)}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.label} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <span className="stcz-field-hint">Applied to titles, product names, and section headers.</span>
              </div>
              <div className="stcz-field">
                <label className="stcz-field-label">Body font</label>
                <select
                  className="stcz-input stcz-select"
                  value={customConfig.bodyFont}
                  onChange={(e) => update('bodyFont', e.target.value)}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.label} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <span className="stcz-field-hint">Applied to product descriptions and body copy.</span>
              </div>
            </>
          )}

          {activeTab === 'buttons' && (
            <>
              <div className="stcz-field">
                <label className="stcz-field-label">Button corner radius</label>
                <select
                  className="stcz-input stcz-select"
                  value={customConfig.buttonBorderRadius}
                  onChange={(e) => update('buttonBorderRadius', e.target.value)}
                >
                  <option value="0">Square (0)</option>
                  <option value="0.25rem">Slightly rounded (0.25rem)</option>
                  <option value="0.5rem">Rounded (0.5rem)</option>
                  <option value="0.75rem">Very rounded (0.75rem)</option>
                  <option value="9999px">Pill (fully rounded)</option>
                </select>
              </div>
              <div className="stcz-field">
                <label className="stcz-field-label">Button padding</label>
                <select
                  className="stcz-input stcz-select"
                  value={customConfig.buttonPadding}
                  onChange={(e) => update('buttonPadding', e.target.value)}
                >
                  <option value="0.5rem 1rem">Compact</option>
                  <option value="0.625rem 1.25rem">Standard</option>
                  <option value="0.75rem 1.5rem">Generous</option>
                  <option value="1rem 2rem">Large</option>
                </select>
              </div>
              <div
                className="stcz-button-preview"
                style={{
                  background: customConfig.primaryColor,
                  borderRadius: customConfig.buttonBorderRadius,
                  padding: customConfig.buttonPadding,
                  fontFamily: customConfig.bodyFont || undefined,
                }}
              >
                Preview button
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ─── Main page ──────────────────────────────────────────────── */
export default function SiteCustomization() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getAxios } = useSecureAuth();
  const { current: store } = useSelector((s) => s.store || {});
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState('colors');
  const [selectedTheme, setSelectedTheme] = useState('MODERN');
  const [isSaving, setIsSaving] = useState(false);
  const [customizingTheme, setCustomizingTheme] = useState(null);

  // Premium marketplace state
  const [premiumThemes, setPremiumThemes] = useState([]);
  const [purchasedThemes, setPurchasedThemes] = useState([]);
  const [activePremiumThemeId, setActivePremiumThemeId] = useState(null);
  const [loadingPremium, setLoadingPremium] = useState(true);
  const [industryFilter, setIndustryFilter] = useState('ALL');
  const [premiumSubTab, setPremiumSubTab] = useState('all');
  const [activatingTheme, setActivatingTheme] = useState(null);
  const [purchasingTheme, setPurchasingTheme] = useState(null);

  useEffect(() => {
    if (store) setSelectedTheme(store.theme || 'MODERN');
  }, [store]);

  /* ── Premium themes fetch ──────────────────────────────────── */
  const fetchPremiumData = useCallback(async () => {
    try {
      setLoadingPremium(true);
      const axios = getAxios();

      const themesRes = await axios.get('/themes', {
        params: { industry: industryFilter !== 'ALL' ? industryFilter : undefined },
      });
      if (themesRes.data?.themes) {
        setPremiumThemes(themesRes.data.themes);
      }

      try {
        const purchasedRes = await axios.get('/themes/merchant/purchased');
        if (purchasedRes.data?.themes) {
          setPurchasedThemes(purchasedRes.data.themes);
        }
      } catch (err) { /* not logged in or no purchases */ }

      try {
        const configRes = await axios.get('/themes/merchant/theme-config');
        if (configRes.data?.themeConfig?.themeId) {
          setActivePremiumThemeId(configRes.data.themeConfig.themeId);
        } else if (configRes.data?.theme?.id) {
          setActivePremiumThemeId(configRes.data.theme.id);
        } else {
          setActivePremiumThemeId(null);
        }
      } catch (err) { /* no premium config */ }
    } catch (err) {
      console.error('Failed to fetch premium themes:', err);
    } finally {
      setLoadingPremium(false);
    }
  }, [getAxios, industryFilter]);

  useEffect(() => {
    if (activeTab === 'premium') fetchPremiumData();
  }, [activeTab, fetchPremiumData]);

  /* ── Free theme handlers ───────────────────────────────────── */
  const activeThemeId = store?.theme || 'MODERN';
  const hasChanges = activeTab === 'colors' && selectedTheme !== activeThemeId;

  const handleApplyTheme = async () => {
    setIsSaving(true);
    try {
      const axios = getAxios();
      const theme = THEMES.find((t) => t.id === selectedTheme);
      const payload = {
        theme: selectedTheme,
        themeConfig: { ...store?.themeConfig, primaryColor: theme.primaryColor },
      };
      await dispatch(createOrUpdateStore({ payload, axiosInstance: axios }));
      await dispatch(fetchMyStore(axios));
      addToast('Theme applied successfully!', 'success');
    } catch (err) {
      console.error('Failed to apply theme:', err);
      addToast('Failed to apply theme. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    setIsSaving(true);
    try {
      const axios = getAxios();
      const payload = { theme: 'MODERN', themeConfig: { primaryColor: '#2563eb' } };
      await dispatch(createOrUpdateStore({ payload, axiosInstance: axios }));
      try { await axios.post('/themes/deactivate'); } catch (_) { /* optional */ }
      await dispatch(fetchMyStore(axios));
      setSelectedTheme('MODERN');
      setActivePremiumThemeId(null);
      addToast('Theme reset to default', 'success');
    } catch (err) {
      console.error('Failed to reset theme:', err);
      addToast('Failed to reset theme. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCustomization = async (config) => {
    setIsSaving(true);
    try {
      const axios = getAxios();
      const payload = { theme: customizingTheme, themeConfig: { ...store?.themeConfig, ...config } };
      await dispatch(createOrUpdateStore({ payload, axiosInstance: axios }));
      await dispatch(fetchMyStore(axios));
      setCustomizingTheme(null);
      addToast('Customisation saved', 'success');
    } catch (err) {
      console.error('Failed to save customization:', err);
      addToast('Failed to save customisation', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Premium theme handlers ────────────────────────────────── */
  const isOwnedPremium = (theme) => {
    if (theme.price === 0) return true;
    return purchasedThemes.some((p) => p.id === theme.id);
  };

  const handleActivatePremium = async (theme) => {
    setActivatingTheme(theme.id);
    try {
      const axios = getAxios();
      const res = await axios.post(`/themes/${theme.id}/activate`);
      if (res.data.success) {
        setActivePremiumThemeId(theme.id);
        addToast('Theme activated successfully!', 'success');
      } else {
        addToast(res.data.error || 'Failed to activate theme', 'error');
      }
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to activate theme', 'error');
    } finally {
      setActivatingTheme(null);
    }
  };

  const handlePurchasePremium = async (theme) => {
    if (theme.price === 0) {
      handleActivatePremium(theme);
      return;
    }
    setPurchasingTheme(theme.id);
    try {
      const axios = getAxios();
      const callbackUrl = `${window.location.origin}/site/themes/purchase/callback`;
      const res = await axios.post(`/themes/${theme.id}/purchase`, { callbackUrl });
      if (res.data.success && res.data.paymentUrl) {
        window.location.href = res.data.paymentUrl;
      } else {
        addToast(res.data.error || 'Failed to initialize payment', 'error');
      }
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to process purchase', 'error');
    } finally {
      setPurchasingTheme(null);
    }
  };

  /* ── Derived ───────────────────────────────────────────────── */
  const filteredPremiumThemes = premiumThemes.filter((t) => {
    if (premiumSubTab === 'free')      return t.price === 0;
    if (premiumSubTab === 'premium')   return t.price > 0;
    if (premiumSubTab === 'purchased') return purchasedThemes.some((p) => p.id === t.id);
    return true;
  });

  const customizingThemeData = THEMES.find((t) => t.id === customizingTheme);

  return (
    <Shell>
      <FeatureGate
        featureId="store.customization"
        upgradeMessage="Upgrade to unlock advanced store customization with themes, colors, fonts, and styling options"
      >
        <div className="stcz-page">

          {/* ── Header ────────────────────────────────────── */}
          <header className="stcz-header">
            <div className="stcz-header-text">
              <h1 className="stcz-title">Store themes</h1>
              <p className="stcz-subtitle">
                Pick a theme, customize colors and fonts, and preview before going live.
              </p>
            </div>
          </header>

          {/* ── Main tabs ─────────────────────────────────── */}
          <div className="stcz-tabs-wrap">
            <div className="stcz-tabs">
              {MAIN_TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`stcz-tab${activeTab === t.id ? ' is-active' : ''}`}
                    onClick={() => setActiveTab(t.id)}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Free themes tab ───────────────────────────── */}
          {activeTab === 'colors' && (
            <section className="stcz-section">
              <div className="stcz-section-head">
                <h2 className="stcz-section-title">Free color themes</h2>
                <p className="stcz-section-desc">Tap a theme to preview, then apply when you're happy.</p>
              </div>
              <div className="stcz-themes">
                {THEMES.map((theme) => (
                  <BrowserMockup
                    key={theme.id}
                    theme={theme}
                    isActive={theme.id === activeThemeId && !activePremiumThemeId}
                    isSelected={theme.id === selectedTheme}
                    onSelect={() => setSelectedTheme(theme.id)}
                    onCustomize={() => setCustomizingTheme(theme.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* ── Premium themes tab ────────────────────────── */}
          {activeTab === 'premium' && (
            <section className="stcz-section">
              <div className="stcz-premium-controls">
                <div className="stcz-premium-subtabs-wrap">
                  <div className="stcz-premium-subtabs">
                    {PREMIUM_SUBTABS.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        className={`stcz-premium-subtab${premiumSubTab === st.id ? ' is-active' : ''}`}
                        onClick={() => setPremiumSubTab(st.id)}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                </div>
                <select
                  className="stcz-input stcz-select stcz-premium-filter"
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  aria-label="Filter by industry"
                >
                  {INDUSTRIES.map((i) => (
                    <option key={i.value} value={i.value}>{i.label}</option>
                  ))}
                </select>
              </div>

              {loadingPremium ? (
                <div className="stcz-loading">
                  <Loader2 size={20} className="stcz-spin" /> Loading themes…
                </div>
              ) : filteredPremiumThemes.length === 0 ? (
                <div className="stcz-empty">
                  <div className="stcz-empty-icon"><Palette size={22} /></div>
                  <h3 className="stcz-empty-title">No themes found</h3>
                  <p className="stcz-empty-desc">
                    {premiumSubTab === 'purchased'
                      ? 'You haven\'t purchased any themes yet. Browse Premium to find one.'
                      : 'Try a different industry filter or sub-tab.'}
                  </p>
                </div>
              ) : (
                <div className="stcz-premium-grid">
                  {filteredPremiumThemes.map((theme) => (
                    <PremiumThemeCard
                      key={theme.id}
                      theme={theme}
                      isOwned={isOwnedPremium(theme)}
                      isActive={activePremiumThemeId === theme.id}
                      onActivate={() => handleActivatePremium(theme)}
                      onPurchase={() => handlePurchasePremium(theme)}
                      onPreview={() => navigate(`/site/themes/${theme.slug}/preview`)}
                      onCustomize={() => navigate('/site/themes/builder')}
                      activating={activatingTheme === theme.id}
                      purchasing={purchasingTheme === theme.id}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── Layouts tab ───────────────────────────────── */}
          {activeTab === 'layouts' && (
            <section className="stcz-section">
              <div className="stcz-section-head">
                <h2 className="stcz-section-title">Layout templates</h2>
                <p className="stcz-section-desc">Industry-tailored storefront layouts. Coming soon.</p>
              </div>
              <div className="stcz-layouts">
                {LAYOUT_TEMPLATES.map((t) => (
                  <LayoutTemplateCard key={t.id} template={t} />
                ))}
              </div>
            </section>
          )}

          {/* ── Active theme footer card (always visible) ── */}
          <section className="stcz-active-card">
            <div className="stcz-active-info">
              <span className="stcz-active-icon">
                <Shield size={16} />
              </span>
              <div>
                <strong>
                  {activePremiumThemeId
                    ? (premiumThemes.find((t) => t.id === activePremiumThemeId)?.name || 'Premium theme')
                    : (THEMES.find((t) => t.id === activeThemeId)?.name || 'MODERN')}
                </strong>
                <em>Currently active on your storefront</em>
              </div>
            </div>
            <button
              type="button"
              className="stcz-btn stcz-btn-secondary"
              onClick={handleResetToDefault}
              disabled={isSaving || (activeThemeId === 'MODERN' && !activePremiumThemeId)}
            >
              <RotateCcw size={13} /> Reset to default
            </button>
          </section>
        </div>

        {/* ── Floating apply bar (Free-tab changes) ───────── */}
        {hasChanges && (
          <div className="stcz-apply-bar" role="region" aria-label="Theme changes pending">
            <span className="stcz-apply-info">
              <span className="stcz-apply-dot" />
              <strong>{THEMES.find((t) => t.id === selectedTheme)?.name}</strong>
              <span className="stcz-apply-info-detail">&nbsp;selected</span>
            </span>
            <div className="stcz-apply-actions">
              <button
                type="button"
                className="stcz-btn stcz-btn-ghost"
                onClick={() => setSelectedTheme(activeThemeId)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="stcz-btn stcz-btn-primary"
                onClick={handleApplyTheme}
                disabled={isSaving}
              >
                {isSaving ? <Loader2 size={14} className="stcz-spin" /> : <Check size={14} />}
                {isSaving ? 'Applying…' : 'Apply theme'}
              </button>
            </div>
          </div>
        )}

        {/* ── Customize modal ──────────────────────────────── */}
        <ThemeCustomizationModal
          theme={customizingThemeData}
          store={store}
          isOpen={!!customizingTheme}
          onClose={() => setCustomizingTheme(null)}
          onSave={handleSaveCustomization}
        />
      </FeatureGate>
    </Shell>
  );
}
